// The in-play HUD and the round cards (intro, call, flash, results). Two authored layouts (studio rule: desktop and
// mobile are designed separately, never one reflowed): body.desk shows keys and more information; body.touch shows a
// thumb layout — stick on the left, big ACTION/POP on the right, and only what a phone can hold. Everything visible is
// the painted kit (ui/kit.ts); text wears the house outline. Screens outside play (door, Burrow, locker…) live in
// screens.ts.

import { h, icon, button, kit } from "./kit";
import * as input from "../input";
import * as audio from "../audio";
import * as store from "../meta/store";
import { Screens } from "./screens";
import { RESIDENTS, INSPECTOR_COSTUME } from "../../../shared/residents";
import type { PublicView, PrivateView, SimEvent } from "../../../shared/sim";
import type { RoundPayout } from "../meta/profile";
import { RARITY_LABEL, type Rarity } from "../meta/catalog";
import * as T from "../../../shared/tuning";
import type { Game } from "../game";

const CSS = `
#hud { position: fixed; inset: 0; pointer-events: none; }
#hud .pe { pointer-events: auto; }
#hud .t { position: absolute; }
.big { font-size: calc(30px + 2vmin); } .mid { font-size: 20px; } .sm { font-size: 14px; }
/* top centre: the clock and the vault */
#clockwrap { top: calc(var(--safe-t) + 8px); left: 50%; transform: translateX(-50%); display: flex; flex-direction: column; align-items: center; gap: 2px; }
#clock { font-size: 40px; line-height: 1; font-variant-numeric: tabular-nums; }
#clock.urgent { animation: pulse .9s ease-in-out infinite; } @keyframes pulse { 50% { transform: scale(1.12); } }
#vault { display: flex; gap: 2px; align-items: center; }
#vault img { width: 26px; height: 26px; } #vault .lab { font-size: 13px; margin-right: 4px; }
/* top left: role + goals */
#role { top: calc(var(--safe-t) + 8px); left: calc(var(--safe-l) + 10px); display: flex; align-items: center; gap: 6px; font-size: 16px; }
#role img { width: 30px; height: 30px; }
#goals { top: calc(var(--safe-t) + 62px); left: calc(var(--safe-l) + 10px); font-size: 15px; display: flex; flex-direction: column; gap: 4px; }
#goals .g { display: flex; align-items: center; gap: 6px; } #goals .g img { width: 22px; height: 22px; } #goals .g.done { opacity: .7; }
/* top right */
#tr { top: calc(var(--safe-t) + 8px); right: calc(var(--safe-r) + 10px); display: flex; gap: 8px; align-items: center; }
#tr .chip { font-size: 14px; padding: 0 4px; white-space: nowrap; }
#tr .btn.round { width: calc(150px * var(--k)); height: calc(150px * var(--k)); display: grid; place-items: center; }
#tr .btn.round img { width: 60%; height: 60%; object-fit: contain; }
#humans { top: calc(var(--safe-t) + 70px); right: calc(var(--safe-r) + 10px); font-size: 13px; white-space: nowrap; }
#call { top: max(24%, calc(var(--safe-t) + 150px)) !important; }
#bAct .bi, #bSprint .bi, #bWhistle .bi { position: absolute; width: 52%; height: 52%; object-fit: contain; pointer-events: none; }
/* bottom: the verb */
#verb { bottom: calc(var(--safe-b) + 18px); left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 10px; font-size: 20px; white-space: nowrap; }
#verb .key { font-size: 16px; padding: 0 8px; }
#hold { position: absolute; left: 0; right: 0; bottom: -10px; height: 8px; }
#charges { bottom: calc(var(--safe-b) + 70px); left: 50%; transform: translateX(-50%); display: flex; gap: 6px; }
#charges .rg { align-self: center; font-size: 14px; padding: 4px 10px; white-space: nowrap; }
#charges img { width: 40px; height: 40px; transition: transform .15s, opacity .15s; } #charges img.off { opacity: .25; transform: scale(.8); }
/* the call card */
#call { top: 22%; left: 50%; transform: translate(-50%, -50%) scale(1); display: flex; align-items: center; gap: 14px; transition: transform .12s; }
#call.pop { animation: callpop .35s cubic-bezier(.2,1.6,.4,1); } @keyframes callpop { from { transform: translate(-50%,-50%) scale(.4); } }
#call img.ci { width: 84px; height: 84px; } #call .w { font-size: 48px; }
#call .bar { position: absolute; left: 12%; right: 12%; bottom: 8px; height: 8px; transform-origin: left; }
/* flash words */
/* the guide: the painted arrow to the nearest capsule (then drain); over the target when on screen, on the edge when not */
#guide { position: absolute; left: 0; top: 0; width: 56px; height: 56px; margin: -28px 0 0 -28px; pointer-events: none; display: grid; place-items: center; }
#guide img { width: 56px; height: 56px; filter: drop-shadow(0 2px 2px rgba(60,30,40,.45)); }
#guide .gl { position: absolute; top: 54px; font-size: 14px; white-space: nowrap; }
#flash { top: 36%; left: 50%; transform: translate(-50%, -50%); font-size: 34px; white-space: nowrap; pointer-events: none; }
#flash.on { animation: flash 1.3s ease-out forwards; } @keyframes flash { 0% { opacity: 0; transform: translate(-50%,-30%) scale(.6); } 12% { opacity: 1; transform: translate(-50%,-50%) scale(1.1); } 22% { transform: translate(-50%,-50%) scale(1); } 80% { opacity: 1; } 100% { opacity: 0; transform: translate(-50%,-70%); } }
/* toasts */
#toasts { top: calc(var(--safe-t) + 110px); right: calc(var(--safe-r) + 10px); display: flex; flex-direction: column; gap: 6px; align-items: flex-end; }
#toasts .toast { font-size: 15px; display: flex; gap: 6px; align-items: center; animation: tin .3s ease-out; } #toasts .toast img { width: 24px; height: 24px; }
@keyframes tin { from { transform: translateX(40px); opacity: 0; } }
/* overlay cards */
.overlay { position: fixed; inset: 0; display: grid; place-items: center; pointer-events: auto; }
.overlay.pass { pointer-events: none; }
.card { max-width: min(560px, 92vw); max-height: calc(100dvh - 24px); text-align: center; display: flex; flex-direction: column; align-items: center; gap: 8px; overflow: auto; }
.card .title { font-size: 38px; line-height: 1.05; } .card .sub { font-size: 18px; color: var(--ink); }
.card .portrait { width: 120px; height: 120px; object-fit: contain; }
.card .row { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; align-items: center; }
.card .stat { display: flex; flex-direction: column; align-items: center; font-size: 15px; min-width: 70px; } .card .stat b { font-size: 26px; }
.card .caps img { width: 40px; height: 40px; }
/* touch controls */
#tc { position: fixed; inset: 0; pointer-events: none; }
#stickvis { position: absolute; width: 120px; height: 120px; margin: -60px 0 0 -60px; opacity: .85; pointer-events: none; }
#stickvis .knob { position: absolute; width: 56px; height: 56px; left: 32px; top: 32px; }
#tc .tb { position: absolute; pointer-events: auto; display: grid; place-items: center; }
#tc .tb img { width: 100%; height: 100%; } #tc .tb .tl { position: absolute; bottom: -18px; font-size: 13px; white-space: nowrap; }
#bAct { width: 96px; height: 96px; right: calc(var(--safe-r) + 22px); bottom: calc(var(--safe-b) + 26px); }
#bSprint { width: 64px; height: 64px; right: calc(var(--safe-r) + 134px); bottom: calc(var(--safe-b) + 22px); }
#bWhistle { width: 64px; height: 64px; right: calc(var(--safe-r) + 134px); bottom: calc(var(--safe-b) + 22px); }
#bAct.hot img { filter: drop-shadow(0 0 10px #fff3b0) drop-shadow(0 0 4px #fff3b0); }
/* touch layout: show less, not smaller */
body.touch #verb .key, body.touch #goals .hint { display: none; }
body.touch #verb { bottom: calc(var(--safe-b) + 132px); font-size: 17px; }
body.touch #clock { font-size: 30px; }
body.touch.land #goals { top: calc(var(--safe-t) + 48px); font-size: 13px; }
body.touch #call img.ci { width: 60px; height: 60px; } body.touch #call .w { font-size: 34px; }
body.touch #flash { font-size: 24px; top: 30%; }
body.touch #toasts { display: none; }
body.touch.port #role { font-size: 14px; } body.touch.port #vault img { width: 20px; height: 20px; }
body.desk #tc { display: none; }
/* small desktop frames (CrazyGames 800–1040 wide): a tighter top band */
@media (max-width: 1100px) { body.desk #clock { font-size: 32px; } body.desk #vault img { width: 20px; height: 20px; } body.desk #role { font-size: 14px; } }
/* phones: the top band holds only what fits; the player count lives in settings */
body.touch #humans { display: none; }
body.touch #vault img { width: 17px; height: 17px; } body.touch #vault .lab { display: none; }
body.touch #clock { font-size: 26px; }
body.touch.land #role { font-size: 12px; } body.touch.land #role img { width: 22px; height: 22px; }
body.touch.land #goals { top: calc(var(--safe-t) + 46px); }
body.touch.land #call { top: max(30%, calc(var(--safe-t) + 120px)) !important; }
body.touch.port #role { top: calc(var(--safe-t) + 62px); font-size: 13px; }
body.touch.port #goals { top: calc(var(--safe-t) + 104px); font-size: 13px; }
body.touch.port #call { top: max(36%, calc(var(--safe-t) + 270px)) !important; }
body.touch.port #tr .btn.round { width: 40px; height: 40px; } body.touch.port #tr { gap: 4px; }
body.touch.port #vault img { width: 14px; height: 14px; } body.touch.port #vault { gap: 1px; }
body.touch.port #charges { bottom: calc(var(--safe-b) + 150px); }
body.touch.port #verb { bottom: calc(var(--safe-b) + 196px); }
@media (max-width: 380px) { body.touch.port #vault img { width: 14px; height: 14px; } body.touch.port #tr .btn.round { width: 40px; height: 40px; } }
`;

/** DOM writes only on change (per-frame writes cost frames on Chromebooks — WARPAINT's lesson, measured again here) */
const setText = (el: HTMLElement, v: string) => { if (el.textContent !== v) el.textContent = v; };
const setSrc = (el: HTMLElement, name: string) => { if (el.dataset.ic !== name) { el.dataset.ic = name; el.setAttribute("src", (icon(name) as HTMLImageElement).src); } };
const toggle = (el: HTMLElement, cls: string, on: boolean) => { if (el.classList.contains(cls) !== on) el.classList.toggle(cls, on); };
const setStyle = (el: HTMLElement, k: string, v: string) => { if ((el.style as any)[k] !== v) (el.style as any)[k] = v; };

export class Hud {
  root: HTMLElement;
  screens: Screens;
  private el: Record<string, HTMLElement> = {};
  private modal: HTMLElement | null = null;
  private lastCallAt = -1;
  private goalsDone = { call: false, grab: false, stash: false };
  private flashT = 0;
  private moved = false;

  constructor(public game: Game) {
    const st = document.createElement("style"); st.textContent = CSS; document.head.appendChild(st);
    this.layoutClass(); addEventListener("resize", () => this.layoutClass());
    const ui = document.getElementById("ui")!;
    this.root = h("div#hud");
    ui.append(this.root);
    const E = this.el;
    E.clockwrap = h("div#clockwrap.t", {}, E.clock = h("div#clock.disp.stroke", {}, "2:30"), E.vault = h("div#vault.k-chip"));
    E.role = h("div#role.t.k-chip.disp", {}, E.roleIco = icon("sneak_mask"), E.roleTxt = h("span"));
    E.goals = h("div#goals.t");
    E.humans = h("div#humans.t.chip.k-chip.disp");
    E.tr = h("div#tr.t", {},
      E.code = h("div.chip.k-chip.disp.pe.hidden"),
      E.snd = button(icon(audio.isMuted() ? "sound_off" : "sound_on"), "btn_round", () => { audio.setMuted(!audio.isMuted()); (E.snd.firstChild as HTMLImageElement).src = (icon(audio.isMuted() ? "sound_off" : "sound_on") as HTMLImageElement).src; }),
      E.menu = button(icon("gear"), "btn_round", () => this.screens.settings()));
    E.snd.classList.add("round", "pe"); E.menu.classList.add("round", "pe");
    E.verb = h("div#verb.t.k-banner_ribbon.disp.hidden", {}, E.verbKey = h("span.key.k-chip"), E.verbTxt = h("span.stroke"));
    E.charges = h("div#charges.t.hidden");
    E.call = h("div#call.t.k-call_card_real.hidden", {}, E.callIco = icon("call_dance", "ci"), E.callW = h("span.w.disp"), E.callBar = h("div.bar.k-bar_fill"));
    E.flash = h("div#flash.t.disp.stroke");
    E.toasts = h("div#toasts.t");
    E.guide = h("div#guide.hidden", {}, E.guideImg = icon("you_arrow"), E.guideTxt = h("span.gl.disp.stroke"));
    this.root.append(E.guide, E.clockwrap, E.role, E.goals, E.tr, E.humans, E.verb, E.charges, E.call, E.flash, E.toasts);
    this.buildTouch();
    this.screens = new Screens(game, this);
  }

  private layoutClass() {
    const touch = input.input.touch || matchMedia("(pointer: coarse)").matches;
    document.body.classList.toggle("touch", touch); document.body.classList.toggle("desk", !touch);
    document.body.classList.toggle("port", innerHeight > innerWidth); document.body.classList.toggle("land", innerWidth >= innerHeight);
  }

  // ---- touch controls (authored thumb layout)
  private buildTouch() {
    const E = this.el;
    E.tc = h("div#tc");
    E.stick = h("div#stickvis.hidden", {}, icon("stick_base"), E.knob = h("div.knob", {}, icon("stick_knob")));
    E.bAct = h("div#bAct.tb", {}, icon("btn_action"), E.bActI = icon("sneak_mask", "bi"), E.bActL = h("span.tl.disp.stroke", {}, "ACTION"));
    E.bSprint = h("div#bSprint.tb", {}, icon("btn_round_cream"), icon("arrow", "bi"), h("span.tl.disp.stroke", {}, "RUN"));
    E.bWhistle = h("div#bWhistle.tb.hidden", {}, icon("btn_whistle"), E.bWhL = h("span.tl.disp.stroke", {}, "WHISTLE"));
    input.bindHold(E.bAct, (d) => { if (this.game["priv"]?.role === "inspector") { if (d) input.input.popFire = true; } else input.pressAction(d); });
    input.bindHold(E.bSprint, (d) => input.setTouchSprint(d));
    input.bindHold(E.bWhistle, (d) => { if (d) input.input.whistle = true; });
    E.tc.append(E.stick, E.bAct, E.bSprint, E.bWhistle);
    this.root.append(E.tc);
  }

  modalOpen() { return !!this.modal || this.screens.open(); }

  // ---- per frame
  update(pub: PublicView, me: PrivateView, dt: number) {
    const E = this.el;
    const left = Math.max(0, Math.ceil(pub.phaseLeft));
    setText(E.clock, pub.phase === "play" ? `${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}` : pub.phase === "intro" ? `${left}` : "");
    toggle(E.clock, "urgent", pub.phase === "play" && left <= 15);
    this.renderVault(pub.vault, pub.goal);
    const insp = me.role === "inspector";
    setSrc(E.roleIco, insp ? "inspector_badge" : "sneak_mask");
    setText(E.roleTxt, insp ? "INSPECTOR" : `SNEAK · ${this.costumeName(me)}`);
    setStyle(E.role, "color", insp ? "var(--insp)" : "var(--plum)");
    const humans = pub.seats.filter((s) => s.human).length;
    setText(E.humans, `${humans} REAL PLAYER${humans === 1 ? "" : "S"}`);
    // the walk hint (first rounds): off for the session once the player has moved
    if (pub.phase === "play" && (input.input.mx || input.input.mz)) this.moved = true;
    let verb = "", key = "";
    if (pub.phase === "play" && !insp) {
      if (me.respawnIn > 0) verb = `BACK IN ${Math.ceil(me.respawnIn)}…`;
      else if (me.can === "answer") { verb = `DO IT: ${pub.call?.kind.toUpperCase() ?? ""}!`; key = "SPACE"; }
      else if (me.can === "freeze") verb = "FREEZE! DON'T MOVE";
      else if (me.can === "grab") { const t = document.body.classList.contains("touch"); verb = this.game.guided() ? (t ? "TAP GRAB TO TAKE THE CAPSULE" : "PRESS TO GRAB THE CAPSULE") : "HOLD TO GRAB"; key = t ? "" : "SPACE"; }
      else if (me.can === "stash") { const t = document.body.classList.contains("touch"); verb = this.game.guided() ? (t ? "TAP STASH TO HIDE IT" : "PRESS TO STASH IT") : "HOLD TO STASH"; key = t ? "" : "SPACE"; }
      else if (me.carry) verb = me.carry === 2 ? "GOLDEN! FIND A DRAIN" : "TAKE IT TO A DRAIN";
      else if (!this.moved && this.game.guided()) { const t = document.body.classList.contains("touch"); verb = t ? "DRAG THE LEFT SIDE TO WALK" : "TO WALK"; key = t ? "" : "WASD / ARROWS"; }
      else if (this.game.guided() && !pub.stalls.some(Boolean)) verb = "CAPSULES POP UP AT THE STALLS";
      else if (this.game.guided()) verb = "WALK TO THE CAPSULE";   // first minute: the arrow's target, in words
    } else if (pub.phase === "play" && insp) {
      verb = input.input.touch ? (me.mark ? "TAP POP!" : "TAP A SUSPECT") : "CLICK A SUSPECT TO POP";
    }
    toggle(E.verb, "hidden", !verb);
    setText(E.verbTxt, verb); setText(E.verbKey, key); toggle(E.verbKey, "hidden", !key);
    toggle(E.charges, "hidden", !insp || pub.phase !== "play");
    if (insp) {
      // the pops, and when the next lost one comes back (owner 10-05: "ran out of the 3 pops and they just refilled?")
      if (E.charges.childElementCount !== T.POP_CHARGES + 1) { E.charges.innerHTML = ""; for (let i = 0; i < T.POP_CHARGES; i++) E.charges.append(icon("pop_charge")); E.charges.append(E.regen = h("span.rg.k-chip.disp")); }
      Array.from(E.charges.querySelectorAll("img")).forEach((c, i) => toggle(c as HTMLElement, "off", i >= me.charges));
      toggle(E.regen, "hidden", me.regenIn < 0);
      if (me.regenIn >= 0) setText(E.regen, `+1 POP IN ${Math.ceil(me.regenIn)}s`);
    }
    if (document.body.classList.contains("touch")) {
      setSrc(E.bAct.querySelector("img")!, insp ? "btn_pop" : "btn_action");
      const actIcon = insp ? "" : me.can === "answer" && pub.call ? "call_" + pub.call.kind : me.can === "grab" ? "capsule_common" : me.can === "stash" ? "burrow" : me.can === "freeze" ? "call_freeze" : "sneak_mask";
      toggle(E.bActI, "hidden", insp);
      if (actIcon) setSrc(E.bActI, actIcon);
      setText(E.bActL, insp ? "POP" : me.can === "grab" ? "GRAB" : me.can === "stash" ? "STASH" : me.can === "answer" ? (pub.call?.kind.toUpperCase() ?? "DO IT") : "ACTION");
      toggle(E.bAct, "hot", (!insp && (me.can === "answer" || me.can === "grab" || me.can === "stash")) || (insp && !!me.mark));
      toggle(E.bSprint, "hidden", insp); toggle(E.bWhistle, "hidden", !insp);
      setText(E.bWhL, me.whistleIn > 0 ? `${Math.ceil(me.whistleIn)}s` : "WHISTLE");
      setStyle(E.bWhistle, "opacity", me.whistleIn > 0 ? "0.5" : "1");
      const st = input.stick;
      toggle(E.stick, "hidden", !st.active);
      if (st.active) { E.stick.style.left = st.bx + "px"; E.stick.style.top = st.by + "px"; E.knob.style.transform = `translate(${Math.max(-40, Math.min(40, st.cx - st.bx))}px, ${Math.max(-40, Math.min(40, st.cy - st.by))}px)`; }
    }
    if (pub.call && pub.call.age < T.CALL_WINDOW) E.callBar.style.transform = `scaleX(${(1 - pub.call.age / T.CALL_WINDOW).toFixed(3)})`;
    const callLive = !!((pub.call && pub.call.age < T.CALL_HOLD) || (pub.whistle && pub.whistle.age < T.WHISTLE_WINDOW + 0.5));
    toggle(E.call, "hidden", !callLive);
    this.renderGoals(me, pub);
    void dt;
  }

  private costumeName(me: PrivateView) {
    const b = this.game.world; void b;
    const pub = (this.game as any).pub as PublicView | null;
    const body = pub?.bodies.find((x) => x.id === me.me);
    if (!body) return "";
    const id = body.costume === INSPECTOR_COSTUME ? "inspector" : RESIDENTS[body.costume] ?? "";
    return id.replace(/_/g, " ").replace("kid balloon", "kid").toUpperCase();
  }

  private renderVault(n: number, goal: number) {
    const E = this.el;
    if (E.vault.childElementCount !== goal + 1) { E.vault.innerHTML = ""; E.vault.append(h("span.lab.disp", {}, "VAULT")); for (let i = 0; i < goal; i++) E.vault.append(icon("vault_slot_empty")); }
    const imgs = E.vault.querySelectorAll("img");
    imgs.forEach((im, i) => setSrc(im as HTMLElement, i < n ? "vault_slot_full" : "vault_slot_empty"));
  }

  /** place the guide arrow: (x, y) in px; `rot` = screen angle toward the target (0 = pointing down, the icon's own) */
  guide(g: { x: number; y: number; rot: number; label: string } | null) {
    const E = this.el;
    toggle(E.guide, "hidden", !g);
    if (!g) return;
    E.guide.style.transform = `translate(${g.x.toFixed(1)}px, ${g.y.toFixed(1)}px)`;
    E.guideImg.style.transform = `rotate(${g.rot.toFixed(1)}deg)`;
    setText(E.guideTxt, g.label);
  }

  /** onboarding goals (first round ever) — the in-gameplay tutorial CrazyGames asks for */
  private renderGoals(me: PrivateView, pub: PublicView) {
    const p = store.get(); const E = this.el;
    const show = pub.onboarding && me.role === "sneak" && pub.phase !== "results";
    toggle(E.goals, "hidden", !show);
    if (!show) return;
    const g = this.goalsDone;
    g.call ||= me.perfects > 0 || me.answered > 0; g.stash ||= me.stashes > 0; g.grab ||= g.stash || me.carry > 0;
    const key = `${+g.call}${+g.grab}${+g.stash}`;
    if (E.goals.dataset.k === key) return; E.goals.dataset.k = key;
    const row = (done: boolean, txt: string) => h("div.g" + (done ? ".done" : ""), {}, icon(done ? "check" : "capsule_locked"), h("span.disp.stroke", {}, txt));
    E.goals.replaceChildren(row(g.call, "Copy the Town Crier's calls"), row(g.grab, "Grab a capsule at a stall"), row(g.stash, "Stash it in a drain"));
    void p;
  }

  // ---- events
  intro(e: Extract<SimEvent, { k: "round_start" }>, me: PrivateView) { this.goalsDone = { call: false, grab: false, stash: false }; this.screens.intro(e, me); }
  call(kind: string, real: boolean) {
    const E = this.el;
    E.call.className = `t ${real ? "k-call_card_real" : "k-call_card_fake"} pop`;
    void E.call.offsetWidth;
    E.callIco.setAttribute("src", (icon(real ? "call_" + kind : "whistle") as HTMLImageElement).src);
    E.callW.textContent = (real ? "" : "WHISTLE: ") + kind.toUpperCase() + "!";
    E.callW.className = "w disp " + (real ? "" : "stroke teal");
  }
  flash(txt: string, colour: "gold" | "red" | "teal") {
    if (!txt) return;
    const f = this.el.flash; f.textContent = txt; f.className = `t disp stroke ${colour}`; void f.offsetWidth; f.classList.add("on");
  }
  toast(txt: string, ic = "coin") { const t = h("div.toast.k-chip.disp", {}, icon(ic), txt); this.el.toasts.append(t); setTimeout(() => t.remove(), 4200); }
  coach(kind: "whistle") { if (kind === "whistle") this.flash("A WHISTLE IS A TRAP! ONLY OBEY THE BELL", "teal"); }
  stashed(r: Rarity, golden: boolean) { this.flash(golden ? "GOLDEN CAPSULE STASHED!" : `${RARITY_LABEL[r]} CAPSULE STASHED!`, "gold"); }
  vault(_n: number, _goal: number) {}
  respawned(costume: number) { this.flash(`NEW DISGUISE: ${(RESIDENTS[costume] ?? "").replace(/_/g, " ").toUpperCase()}`, "teal"); }
  results(e: Extract<SimEvent, { k: "results" }>, pay: RoundPayout) { this.screens.results(e, pay); }
  door(show: boolean) { this.screens.door(show); }
  setModal(m: HTMLElement | null) { this.modal = m; }
  kitHas(name: string) { return !!kit.frames[name] || !!kit.icons?.[name]; }
}
export { button };
